import { TIER_LABELS, type WorkerTier } from '../../core/index.js'
import type { AvailableShift, DraftSchedule, ShiftPreferencesView } from '../types.js'
import { describePeriod } from './preferencesText.js'

/**
 * Where bidding stands for the worker - the facility's preferences window under its scheduling rules, which
 * replaced the per-tier bid window: a tier the facility assigns does not bid at all; anyone else bids for the
 * months taking preferences that the facility has published shifts for from today on (next month first), and
 * waits for the next month's shifts otherwise. The same rule decides which months the wizard offers.
 */
export type BiddingStatus =
  | { kind: 'assigned'; tier: WorkerTier }
  /** The facility runs a bid window and the month's has not opened yet (bid window plan phase 1). */
  | { kind: 'upcoming'; tier: WorkerTier; period: string; opensAt: string; closesAt: string | null; timezone: string | null }
  | { kind: 'open'; tier: WorkerTier; periods: string[]; submitted: boolean }
  | { kind: 'waiting'; tier: WorkerTier; period: string }

/**
 * The months taking preferences, earliest first: those whose close is still ahead (since 17 October 2026 a month's
 * close shuts it), as the API answers them. An older API answered only this month and next.
 */
export function acceptingPeriods(view: Pick<ShiftPreferencesView, 'accepting' | 'periodsOpen'>): string[] {
  return view.accepting ?? [view.periodsOpen.next, view.periodsOpen.current]
}

/** The months bidding is open for: those taking preferences with published shifts from today on, earliest first. */
export function openBiddingPeriods(view: ShiftPreferencesView, shifts: readonly AvailableShift[], today: string): string[] {
  const published = new Set(shifts.filter((shift) => shift.date >= today).map((shift) => shift.date.slice(0, 7)))
  return acceptingPeriods(view).filter((period) => published.has(period))
}

/** "Nov 3, 6:00 AM" in the facility's zone: a bid window opens and closes at a time of day, so the time matters. */
function momentOf(iso: string, timezone: string | null | undefined): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', ...(timezone ? { timeZone: timezone } : {}) })
}

/**
 * "Preferences for December close Nov 3", in the facility's zone, for the wizard and the Shifts card - with the time
 * under a bid window, "Bidding for December closes Nov 3, 6:00 AM"; null once the month no longer takes saves, or from an older API.
 */
export function preferencesCloseLine(view: Pick<ShiftPreferencesView, 'closesAt' | 'period' | 'timezone' | 'acceptingSaves' | 'opensAt'>): string | null {
  if (!view.closesAt || view.acceptingSaves === false) {
    return null
  }
  if (view.opensAt) {
    return `Bidding for ${monthOf(view.period)} closes ${momentOf(view.closesAt, view.timezone)}`
  }
  const day = new Date(view.closesAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(view.timezone ? { timeZone: view.timezone } : {}) })
  return `Preferences for ${monthOf(view.period)} close ${day}`
}

export function biddingStatusOf(view: ShiftPreferencesView, shifts: readonly AvailableShift[], today: string, draft: DraftSchedule | null, now: Date = new Date()): BiddingStatus {
  // An API older than the scheduling rules answers no `bids`: every tier bid then.
  const tier = view.limits?.tier ?? 'gold'
  if (view.limits?.bids === false) {
    return { kind: 'assigned', tier }
  }
  const periods = openBiddingPeriods(view, shifts, today)
  // Under a bid window the month waits for its opening, whatever is published: the view's month is the one bid for next.
  if (periods.length === 0 && view.opensAt && Date.parse(view.opensAt) > now.getTime()) {
    return { kind: 'upcoming', tier, period: view.period, opensAt: view.opensAt, closesAt: view.closesAt ?? null, timezone: view.timezone ?? null }
  }
  if (periods.length === 0) {
    return { kind: 'waiting', tier, period: acceptingPeriods(view)[0] ?? view.periodsOpen.next }
  }
  return { kind: 'open', tier, periods, submitted: draft !== null && draft.status === 'accepted' && draft.period === periods[0] }
}

function monthOf(period: string): string {
  return describePeriod(period).split(' ')[0] ?? period
}

/** One line saying where bidding stands. */
export function biddingHeadline(status: BiddingStatus): string {
  switch (status.kind) {
    case 'assigned':
      return 'Your facility assigns your schedule'
    case 'upcoming':
      return `Bidding for ${monthOf(status.period)} opens ${momentOf(status.opensAt, status.timezone)}`
    case 'waiting':
      return `Bidding opens when ${monthOf(status.period)}’s shifts are published`
    case 'open':
      return status.submitted ? `${monthOf(status.periods[0] ?? '')} schedule submitted` : `Bid now for ${status.periods.map(monthOf).join(' and ')}`
  }
}

/** What it means for the worker, in a sentence. */
export function biddingDetail(status: BiddingStatus): string {
  switch (status.kind) {
    case 'assigned':
      return `${TIER_LABELS[status.tier]} workers don’t bid. Your month is built from the facility’s rules: weekends first, then weekdays.`
    case 'upcoming':
      return status.closesAt
        ? `Everyone bids in the same window, until ${momentOf(status.closesAt, status.timezone)}. You’ll get a message when it opens.`
        : 'Everyone bids in the same window. You’ll get a message when it opens.'
    case 'waiting':
      return `Your facility hasn’t published ${describePeriod(status.period)} yet. You can tell your scheduler what you want once it does.`
    case 'open':
      return status.submitted ? 'Your scheduler builds the month from it. You can change it until then.' : 'Tell your scheduler what you want, then build and submit your schedule.'
  }
}
